import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { PartyListItem, RatingMatrixService } from '../../services';
import { escapeHtml } from '../../utils/escape-html.utils';

/**
 * Confirms deleting a fee-recipient party. When the API refuses because the party is still
 * assigned (code PARTY_IN_USE), the error toast lists exactly where so the admin can reassign those first.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-delete-party-dialog',
  templateUrl: './delete-party.dialog.html',
  standalone: false,
})
export class DeletePartyDialog {
  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @Output() closed = new EventEmitter<boolean>();

  party: PartyListItem | null = null;
  deleting = false;

  constructor(
    private ratingMatrixSvc: RatingMatrixService,
    private toastSvc: ToastService
  ) {}

  get subtitle(): string {
    const name = this.party ? escapeHtml(this.party.name) : 'this party';
    return `Are you sure you want to delete <strong>${name}</strong>? Issued contracts keep the name they were recorded with.`;
  }

  open(party: PartyListItem) {
    this.party = party;
    this.deleting = false;
    this.dialog.open();
  }

  cancel() {
    this.dialog.close();
    this.closed.emit(false);
  }

  async delete() {
    if (!this.party) return;

    this.deleting = true;
    try {
      await this.ratingMatrixSvc.deleteParty(this.party.id);
      this.toastSvc.show({
        type: 'success',
        title: 'Party deleted',
        text: `<strong>${escapeHtml(this.party.name)}</strong> has been deleted.`,
      });
      this.dialog.close();
      this.closed.emit(true);
    } catch (err) {
      const body = (err as { error?: { code?: string; message?: string; errors?: string[] } })?.error;
      const usage = body?.code === 'PARTY_IN_USE' ? (body.errors ?? []) : [];
      this.toastSvc.show({
        type: 'error',
        title: usage.length ? `${escapeHtml(this.party.name)} is still in use` : 'Unable to delete party',
        // The toast renders raw HTML (no sanitizer), so everything user-entered is escaped.
        text: usage.length
          ? `Reassign or clear these first:<br>${usage.map(line => `• ${escapeHtml(line)}`).join('<br>')}`
          : escapeHtml(body?.message ?? 'Something went wrong.'),
      });
      // Nothing to retry while it is in use; any other failure leaves the dialog open to try again.
      if (usage.length) {
        this.dialog.close();
        this.closed.emit(false);
      }
    } finally {
      this.deleting = false;
    }
  }
}
